/**
 * Dashboard hint when a geocoder label disagrees with the feed address.
 *
 * The feed line is what the client sent (re-cased only when it was shouted).
 * The geocoder's `matchedAddress` stays on `job.location` and is never copied
 * back onto the feed, the cover sheet, or a published payload. This module
 * only decides whether the dashboard should show Clinton's parenthetical:
 *
 * `24 Mclaughlin AV (*Geolocates as McLaughlan Ave)`
 *
 * Case, punctuation, street-type synonyms (`AV` / `Ave` / `Avenue`) and a
 * trailing ", Australia" are not a difference. State and postcode are parsed
 * off the label so "NSW" and "New South Wales" do not read as a different
 * locality. The hint is the geocoder's own street (and locality, when that
 * differs) — the feed text beside it is unchanged.
 */
import { normaliseAddressText } from './address-text.js';
const STATE_FULL = 'australian capital territory|new south wales|northern territory|south australia|western australia|queensland|tasmania|victoria';
const STATE_ABBREV = 'nsw|vic|qld|sa|wa|tas|nt|act';
/**
 * Full state names only come off a comma-separated piece (`", Victoria"`),
 * so a suburb such as "Lake Victoria" is not eaten. Abbreviations may also
 * sit after a space (`"Macquarie Fields NSW"`).
 */
const STATE_TAIL = new RegExp(`(?:,\\s*(?:${STATE_FULL}|${STATE_ABBREV})|\\s+(?:${STATE_ABBREV}))\\s*$`, 'i');
const POSTCODE_TAIL = /(?:,\s*|\s+)(\d{4})\s*$/;
/**
 * `*Geolocates as …` without the wrapping parentheses, or undefined when the
 * geocoded street and locality agree with the feed (or no label was stored).
 */
export function geolocatedAddressHint(address, matchedAddress) {
    const label = matchedAddress?.trim();
    if (!label)
        return undefined;
    const parsed = parseMatchedLabel(label);
    if (!parsed)
        return undefined;
    const parts = [];
    if (parsed.street &&
        normaliseAddressText(address.line1) !== normaliseAddressText(parsed.street)) {
        parts.push(differingStreet(address.line1, parsed.street));
    }
    if (parsed.locality &&
        normaliseAddressText(address.suburb) !== normaliseAddressText(parsed.locality)) {
        parts.push(parsed.locality);
    }
    if (parts.length === 0)
        return undefined;
    return `*Geolocates as ${parts.join(', ')}`;
}
/**
 * Feed street line, plus the hint when there is one. The line itself is the
 * stored feed text, not a re-cased or geocoder-substituted address.
 */
export function addressLineWithGeolocateHint(address, matchedAddress) {
    const hint = geolocatedAddressHint(address, matchedAddress);
    if (!hint || !address.line1)
        return address.line1;
    return `${address.line1} (${hint})`;
}
function parseMatchedLabel(label) {
    let text = label
        .replace(/(?:,\s*)?australia\s*$/i, '')
        .replace(/,\s*$/, '')
        .trim();
    if (!text)
        return undefined;
    // "NSW 2564", "2564 NSW" and ", NSW, 2564" all occur on geocoder labels.
    for (let pass = 0; pass < 4; pass++) {
        const postcode = POSTCODE_TAIL.exec(text);
        if (postcode?.index !== undefined) {
            text = text.slice(0, postcode.index).replace(/[,\s]+$/, '');
            continue;
        }
        const state = STATE_TAIL.exec(text);
        if (state?.index !== undefined) {
            text = text.slice(0, state.index).replace(/[,\s]+$/, '');
            continue;
        }
        break;
    }
    const comma = text.indexOf(',');
    const street = (comma === -1 ? text : text.slice(0, comma)).trim();
    const localityRaw = comma === -1 ? '' : text.slice(comma + 1);
    const locality = localityRaw
        .replace(/,?\s*\d{4}\s*$/, '')
        .split(',')[0]
        ?.trim() ?? '';
    if (!street && !locality)
        return undefined;
    return { street, locality };
}
/** Geocoder street, without a house number both sides already share. */
function differingStreet(feedLine, geocodedStreet) {
    const feedNumber = leadingNumber(feedLine);
    const geocodedNumber = leadingNumber(geocodedStreet);
    if (feedNumber && geocodedNumber && feedNumber.toLowerCase() === geocodedNumber.toLowerCase()) {
        const rest = geocodedStreet
            .trim()
            .slice(geocodedNumber.length)
            .replace(/^[\s,/]+/, '');
        if (rest)
            return rest;
    }
    return geocodedStreet.trim();
}
function leadingNumber(line) {
    const match = /^(\d+[a-zA-Z]?(?:\/\d+[a-zA-Z]?)?)/.exec(line.trim());
    return match?.[1];
}
//# sourceMappingURL=address-hint.js.map